import type { ClientErrorReport } from '../api/clientErrors';
import { APP_VERSION } from '../version';

/** A component that throws on every render would otherwise report on every render. */
const MAX_REPORTS = 10;

/** How much of a stack is kept. Enough to identify the frame; not enough to fill the log with one error. */
const MAX_STACK = 4000;

type Reporter = (report: ClientErrorReport) => void;

/**
 * Module state because there is one window and one throttle to share: a render loop that trips both an error
 * boundary and the window handler should be one report, not two per frame.
 */
let send: Reporter = () => {};

interface Installation {
  report: Reporter;
  /** Defaults to window; injected so the tests do not have to install handlers on the real one. */
  target?: EventTarget;
}

/**
 * Returns the function that removes the handlers again. Identical failures are reported once - an error boundary
 * and the window handler often see the same throw, and a render loop sees it every frame.
 */
export function installErrorReporting({ report, target = window }: Installation): () => void {
  const seen = new Set<string>();
  let reported = 0;

  send = (candidate: ClientErrorReport) => {
    if (reported >= MAX_REPORTS) {
      return;
    }

    const signature = `${candidate.kind ?? ''}|${candidate.message}|${(candidate.stack ?? '').slice(0, 200)}`;
    if (seen.has(signature)) {
      return;
    }

    seen.add(signature);
    reported += 1;
    report(candidate);
  };

  const onError = (event: Event) => {
    const error = event as ErrorEvent;

    // A failed image or stylesheet also raises "error", on the element rather than on the window. Those are
    // not exceptions and they arrive with none of this filled in.
    if (!error.message && !error.error) {
      return;
    }

    send(describe(error.error, error.message, sourceOf(error)));
  };

  const onRejection = (event: Event) => {
    const rejection = event as PromiseRejectionEvent;
    const reason: unknown = rejection.reason;

    if (isExpectedAuthFailure(reason)) {
      return;
    }

    send({
      ...describe(reason, 'An operation failed and nothing handled it.'),
      kind: kindOf(reason) ?? 'UnhandledRejection',
    });
  };

  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);

  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
    send = () => {};
  };
}

/** Through the same throttle, so a boundary and the window handler seeing one throw is one report. */
export function reportCaughtError(thrown: unknown, componentStack?: string): void {
  const report = describe(thrown, 'The interface stopped rendering.');

  send({
    ...report,
    // The component stack says which part of the interface gave way, which a JavaScript stack of minified
    // frames often does not.
    stack: [report.stack, componentStack].filter(Boolean).join('\n\nComponent stack:').slice(0, MAX_STACK),
  });
}

/** A rejected promise can carry an Error, a string, or an object with nothing on it - still worth recording. */
export function describe(thrown: unknown, fallbackMessage: string, source?: string): ClientErrorReport {
  const error = thrown instanceof Error ? thrown : undefined;

  return {
    kind: kindOf(thrown),
    message: messageOf(thrown) || fallbackMessage,
    stack: error?.stack?.slice(0, MAX_STACK),
    route: typeof window === 'undefined' ? undefined : window.location.pathname,
    source,
    version: APP_VERSION,
  };
}

function kindOf(thrown: unknown): string | undefined {
  if (thrown instanceof Error) {
    return thrown.name;
  }

  return typeof thrown === 'string' ? 'string' : undefined;
}

function messageOf(thrown: unknown): string {
  if (thrown instanceof Error) {
    return thrown.message;
  }

  if (typeof thrown === 'string') {
    return thrown;
  }

  return '';
}

function sourceOf(error: ErrorEvent): string | undefined {
  if (!error.filename) {
    return undefined;
  }

  return `${error.filename}:${error.lineno ?? 0}:${error.colno ?? 0}`;
}

/**
 * An expired session is not a defect. The API client answers a 401 by starting a sign-in redirect and
 * rejecting, which can land here while the page is already on its way out.
 */
function isExpectedAuthFailure(reason: unknown): boolean {
  if (typeof reason !== 'object' || reason === null) {
    return false;
  }

  const candidate = reason as { name?: unknown; status?: unknown };
  return candidate.name === 'ApiError' && (candidate.status === 401 || candidate.status === 403);
}
